import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import { LocalBusinessSchema, BreadcrumbSchema } from "../components/SchemaMarkup";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Calendar,
  MessageSquare,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        source: "contact-page",
      });
      toast.success(response.data.message);
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      value: "info@optimagrowth.agency",
      link: "mailto:info@optimagrowth.agency",
      description: "We typically respond within 2-4 hours",
    },
    {
      icon: Phone,
      title: "Call or WhatsApp",
      value: "+27 66 122 9790",
      link: "https://wa.me/27661229790",
      description: "Mon-Fri, 9am-5pm SAST",
    },
    {
      icon: MapPin,
      title: "Location",
      value: "Cape Town, South Africa",
      description: "Serving clients globally",
    },
    {
      icon: Clock,
      title: "Response Time",
      value: "Within 24 hours",
      description: "Usually much faster",
    },
  ];

  const services = [
    { value: "growth-strategy", label: "Business Growth Strategy" },
    { value: "marketing-automation", label: "Marketing Automation" },
    { value: "sales-funnels", label: "Sales Funnels & CRM" },
    { value: "lead-generation", label: "Lead Generation" },
    { value: "seo-content", label: "SEO & Content" },
    { value: "paid-media", label: "Paid Media (Meta/Google)" },
    { value: "workflow-automation", label: "Workflow Automation" },
    { value: "ai-operations", label: "AI-Assisted Operations" },
    { value: "custom", label: "Custom Solution" },
    { value: "other", label: "Other / Not Sure" },
  ];

  return (
    <div className="pt-32" data-testid="contact-page">
      <SEO 
        title="Contact OptimaGrowth | Cape Town Marketing Agency"
        description="Get in touch with OptimaGrowth in Cape Town. Book a free marketing audit or discuss your growth goals with our team."
        keywords="contact OptimaGrowth, free SEO audit, marketing consultation Cape Town, book a strategy call, digital marketing agency contact"
      />
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Contact" }]} />
      <Breadcrumb items={[{ name: "Contact" }]} />
      {/* Hero Section */}
      <section className="section pb-0" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">Contact Us</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                Get a Free SEO & Marketing Audit
              </h1>
              <p className="text-lg text-slate-600">
                Ready to transform your business with systematic growth? Get in touch and let's discuss your goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-2xl font-bold text-optima-dark mb-8">
                Get in Touch
              </h2>

              <div className="grid gap-6 mb-10">
                {contactInfo.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-optima-blue/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-optima-blue" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-optima-dark">{item.title}</h3>
                      {item.link ? (
                        <a
                          href={item.link}
                          className="text-optima-blue hover:underline"
                          target={item.link.startsWith("http") ? "_blank" : undefined}
                          rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                          data-testid={`contact-link-${item.title.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-slate-700">{item.value}</p>
                      )}
                      <p className="text-sm text-slate-500">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-optima-dark mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <a
                    href="https://wa.me/27661229790"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-optima-blue transition-colors"
                    data-testid="whatsapp-btn"
                  >
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    <span className="font-medium">Chat on WhatsApp</span>
                    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                  </a>
                  <a
                    href="https://calendly.com/optimagrowth-info/15min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-optima-blue transition-colors"
                    data-testid="calendar-btn"
                  >
                    <Calendar className="w-5 h-5 text-optima-blue" />
                    <span className="font-medium">Book a Free Discovery Call</span>
                    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8">
                <h2 className="font-heading text-2xl font-bold text-optima-dark mb-2">
                  Send Us a Message
                </h2>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and we'll get back to you shortly.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="input-field"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="input-field"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name
                      </label>
                      <Input
                        placeholder="Acme Inc."
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="input-field"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <Input
                        placeholder="+27 00 000 0000"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-field"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Service Interested In
                    </label>
                    <Select
                      value={formData.service}
                      onValueChange={(value) => setFormData({ ...formData, service: value })}
                    >
                      <SelectTrigger className="input-field" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service..." />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.value} value={service.value}>
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      placeholder="Tell us about your business, goals, and how we can help..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      className="input-field min-h-[150px]"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-optima-blue hover:bg-optima-blue-dark py-6"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Send Message <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-slate-500 text-center">
                    By submitting this form, you agree to our{" "}
                    <a href="/privacy" className="text-optima-blue hover:underline">
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Calendly Booking Section */}
      <section className="section bg-slate-50" data-testid="calendly-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h2 className="font-heading text-2xl font-bold text-optima-dark mb-3">
              Book a Free SEO & Marketing Strategy Call
            </h2>
            <p className="text-slate-600">
              Schedule a 15-minute discovery call with our team. We'll discuss your SEO goals, marketing automation needs, and how OptimaGrowth can help your business grow.
            </p>
          </div>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="calendly-widget">
            <iframe
              src="https://calendly.com/optimagrowth-info/15min?hide_gdpr_banner=1"
              width="100%"
              height="650"
              frameBorder="0"
              title="Book a free SEO and marketing automation strategy call with OptimaGrowth"
            />
          </div>
        </div>
      </section>

      {/* Explore More */}
      <section className="section-alt" data-testid="contact-explore">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl font-bold text-optima-dark mb-6">
              Explore While You Wait
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services" className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:border-optima-blue/30 transition-colors" data-testid="explore-services-link">Our Services</Link>
              <Link to="/pricing" className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:border-optima-blue/30 transition-colors" data-testid="explore-pricing-link">Pricing Packages</Link>
              <Link to="/case-studies" className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:border-optima-blue/30 transition-colors" data-testid="explore-case-studies-link">Case Studies</Link>
              <Link to="/blog" className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:border-optima-blue/30 transition-colors" data-testid="explore-blog-link">Blog</Link>
              <Link to="/software" className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:border-optima-blue/30 transition-colors" data-testid="explore-software-link">Our Software</Link>
              <Link to="/client-onboarding" className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:border-optima-blue/30 transition-colors" data-testid="explore-onboarding-link">Client Onboarding</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
